import { Button, Combobox, useFormField } from "@playbookai/ui";

export interface ClearableComboboxProps {
  readonly options: ReadonlyArray<{ value: string; label: string }>;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly emptyMessage: string;
  readonly clearLabel: string;
}

/**
 * A Combobox plus an explicit Clear.
 *
 * `Combobox` is single-select with no clear affordance — emptying its text and
 * blurring reverts to the current selection — so an OPTIONAL choice made by
 * accident would otherwise be unrepeatable. The button is rendered only when
 * there is something to clear, and is named after its field so a screen reader
 * hears which one it clears.
 *
 * Shared by the Create and Edit pages (PB-2941). Extracted from `CreateKpiPage`
 * when the second consumer arrived rather than in anticipation of one: it is the
 * same three fields with the same clear semantics on both, and a divergence
 * between them would be a bug, not a variation.
 *
 * IT MUST FORWARD `FormField`'s ID, and that is not optional bookkeeping.
 * `FormField` has two integration paths: it `cloneElement`s its single child
 * with the generated `id`, and it publishes the same id on `FormFieldContext`
 * for controls whose structure the clone cannot reach. This component is a
 * WRAPPER `<div>`, so the clone lands the id on the div while the label's
 * `htmlFor` points at it — a label pointing at a non-labelable element, which
 * associates with nothing. Every "Category" and "KPI Type" field on the Create
 * and Edit pages was unlabelled for screen readers because of it. Reading the
 * context and passing `id` down to the real `<input role="combobox">` is the
 * sanctioned second path, and is what makes `getByLabelText` — and a screen
 * reader — find the control.
 */
export function ClearableCombobox({
  options,
  value,
  onChange,
  placeholder,
  emptyMessage,
  clearLabel,
}: ClearableComboboxProps): JSX.Element {
  const field = useFormField();
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <Combobox
          // Conditional spread, not `id={field?.id}`: `exactOptionalPropertyTypes`
          // rejects an explicit `undefined` for an optional prop, and outside a
          // FormField the Combobox must keep minting its own id.
          {...(field?.id ? { id: field.id } : {})}
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          emptyMessage={emptyMessage}
        />
      </div>
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange("")}
        >
          {clearLabel}
        </Button>
      ) : null}
    </div>
  );
}
